/**
 * Cash Flow - Excel export
 *
 * Exports the monthly detail tree (Kode; Activity; Company; Period;
 * Budget; Realized; Unrealized) as a styled .xlsx, mirroring the Portal
 * Bomba layout described in the reference design.
 */

import * as XLSX from 'xlsx-js-style';
import { CashFlowDetailRow } from '../types';
import { buildTree, flattenTree, getMonthName } from '../utils';
import { trackDownload } from '../../activity';

const headerStyle = {
    font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
    fill: { fgColor: { rgb: '0F5C32' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: {
        top: { style: 'thin', color: { rgb: 'CCCCCC' } },
        bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
        left: { style: 'thin', color: { rgb: 'CCCCCC' } },
        right: { style: 'thin', color: { rgb: 'CCCCCC' } },
    },
};

const titleStyle = {
    font: { bold: true, sz: 14, color: { rgb: '0F5C32' } },
};

interface ExportMeta {
    companyName: string;
    year: number;
    month: number;
}

export const exportToExcel = (detail: CashFlowDetailRow[], meta: ExportMeta): void => {
    const tree = buildTree(detail);
    const flat = flattenTree(tree);
    const periodLabel = `${getMonthName(meta.month)} ${meta.year}`;

    const wsData: any[][] = [
        [`CASH FLOW - ${meta.companyName} - ${periodLabel}`],
        [],
        ['Kode', 'Cashflow Activity', 'Company', 'Period', 'Budget', 'Realized', 'Unrealized'],
    ];

    flat.forEach((node) => {
        const indentName = `${'  '.repeat(Math.max(node.depth - 1, 0))}${node.name}`;
        wsData.push([
            node.code,
            indentName,
            meta.companyName,
            periodLabel,
            node.budget,
            node.realized,
            node.unrealized,
        ]);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(wsData);
    worksheet['!cols'] = [
        { wch: 12 }, { wch: 48 }, { wch: 24 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 },
    ];
    worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }];

    // Title style
    const titleCell = XLSX.utils.encode_cell({ r: 0, c: 0 });
    if (worksheet[titleCell]) worksheet[titleCell].s = titleStyle;

    // Header row style (row index 2)
    for (let c = 0; c < 7; c++) {
        const cell = XLSX.utils.encode_cell({ r: 2, c });
        if (worksheet[cell]) worksheet[cell].s = headerStyle;
    }

    // Bold the header (is_header) rows; number format for value columns.
    flat.forEach((node, i) => {
        const r = 3 + i;
        if (node.is_header) {
            for (let c = 0; c < 7; c++) {
                const cell = XLSX.utils.encode_cell({ r, c });
                if (worksheet[cell]) {
                    worksheet[cell].s = { ...(worksheet[cell].s || {}), font: { bold: true } };
                }
            }
        }
        for (let c = 4; c <= 6; c++) {
            const cell = XLSX.utils.encode_cell({ r, c });
            if (worksheet[cell]) {
                worksheet[cell].z = '#,##0';
                worksheet[cell].s = { ...(worksheet[cell].s || {}), alignment: { horizontal: 'right' } };
            }
        }
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Cash Flow');

    const safeCompany = meta.companyName.replace(/[^a-zA-Z0-9]/g, '');
    const filename = `CashFlow_${safeCompany}_${meta.year}${String(meta.month).padStart(2, '0')}.xlsx`;
    XLSX.writeFile(workbook, filename);
    trackDownload('Laporan Cash Flow', { filename });
};
